import axios, { type AxiosError } from 'axios';

/**
 * Parse Retry-After from an axios error (seconds, per RFC 7231).
 * Returns milliseconds to wait, or undefined if not present / not a number.
 */
export function getRetryAfterMs(error: unknown): number | undefined {
  const ax = error as AxiosError | undefined;
  const raw = ax?.response?.headers?.['retry-after'] ?? ax?.response?.headers?.['Retry-After'];
  if (raw == null) return undefined;
  const sec = typeof raw === 'string' ? Number(raw.trim()) : Number(raw);
  if (!Number.isFinite(sec) || sec < 0) return undefined;
  return Math.round(sec * 1000);
}

type ApiErrorBody = {
  message?: string;
  error?: string;
  detail?: string;
  msg?: string;
};

/**
 * Maps axios/network errors to short, user-safe copy (no stack traces).
 * Use in UI banners/toasts; keep server messages when they are plain text.
 */
export function getUserFacingApiMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  const ax = error as AxiosError<ApiErrorBody> | undefined;
  const status = ax?.response?.status;

  if (status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (status === 503) {
    return 'Service temporarily unavailable. Please try again shortly.';
  }
  if (status != null && status >= 500) {
    return 'The server had a problem. Please try again in a few minutes.';
  }
  if (status === 401 || status === 403) {
    return 'You do not have permission to perform this action, or your session expired. Sign in again.';
  }

  const data = ax?.response?.data;
  if (data && typeof data === 'object' && data !== null) {
    const o = data as ApiErrorBody;
    const msg = o.message ?? o.error ?? o.detail ?? o.msg;
    if (typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
  }

  const code = ax?.code;
  const msg = ax?.message;
  if (msg === 'Network Error' || code === 'ERR_NETWORK') {
    return 'Network error. Check your connection and try again.';
  }
  if (code === 'ECONNABORTED' || msg?.toLowerCase().includes('timeout')) {
    return 'Request timed out. Try again.';
  }

  if (typeof msg === 'string' && msg.trim() && status != null && status >= 400) {
    return msg.trim();
  }

  // Thrown errors from app code (e.g. auth) — not axios (guard: tests may mock axios without isAxiosError)
  const notAxios =
    typeof axios.isAxiosError !== 'function' || !axios.isAxiosError(error);
  if (notAxios && error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
}
